import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetPassword } from '../../redux/api';
import { aradugbo_icon } from "../../constants/images";

const Reset = () => {
  const [status, setStatus] = useState('reset'); // loading, success, error, reset
  const [errorMessage, setErrorMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const oobCode = params.get('oobCode');

    if (!mode || !oobCode) {
      setStatus('error');
      setErrorMessage('Invalid or missing parameters in the URL.');
      return;
    }
  }, [location]);

  const validatePassword = (password) => {
    const errors = {};
    
    if (password.length < 8) {
      errors.length = 'Password must be at least 8 characters long';
    }
    
    if (!/(?=.*[a-z])/.test(password)) {
      errors.lowercase = 'Password must contain at least one lowercase letter';
    }
    
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.uppercase = 'Password must contain at least one uppercase letter';
    }
    
    if (!/(?=.*\d)/.test(password)) {
      errors.number = 'Password must contain at least one number';
    }
    
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.special = 'Password must contain at least one special character (@$!%*?&)';
    }
    
    return errors;
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    
    if (password) {
      const errors = validatePassword(password);
      setValidationErrors(prev => ({
        ...prev,
        password: Object.keys(errors).length > 0 ? errors : null
      }));
    } else {
      setValidationErrors(prev => ({
        ...prev,
        password: null
      }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const confirmPwd = e.target.value;
    setConfirmPassword(confirmPwd);
    
    if (confirmPwd && newPassword && confirmPwd !== newPassword) {
      setValidationErrors(prev => ({
        ...prev,
        confirm: 'Passwords do not match'
      }));
    } else {
      setValidationErrors(prev => ({
        ...prev,
        confirm: null
      }));
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    // Validate passwords before submission
    const passwordErrors = validatePassword(newPassword);
    const confirmError = newPassword !== confirmPassword ? 'Passwords do not match' : null;
    
    if (Object.keys(passwordErrors).length > 0 || confirmError) {
      setValidationErrors({
        password: Object.keys(passwordErrors).length > 0 ? passwordErrors : null,
        confirm: confirmError
      });
      return;
    }
    
    setStatus('loading');
    const params = new URLSearchParams(location.search);
    const oobCode = params.get('oobCode');

    try {
      const response = await resetPassword({ code: oobCode, password: newPassword });
      if (response.status === 200 || response.status === 201) {
        setStatus('success');
      } else {
        throw new Error(response.data?.message || 'Password reset failed.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          'Failed to reset password.'
      );
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  return (
    <div 
      className="flex justify-center items-center min-h-screen"
      style={{ backgroundColor: '#EAECF0' }}
    >
      <div 
                className="p-8 rounded-lg shadow-lg max-w-md w-full flex flex-col gap-8"
        style={{ backgroundColor: '#FCFCFD' }}
      >
        <div className='flex w-full justify-center items-center'>

        <img src={aradugbo_icon} alt="Arad" className="max-h-6 w-20" />
                </div>

        {status === 'loading' && (
          <div className="flex justify-center">
            <svg
              className="animate-spin h-8 w-8"
              style={{ color: '#FC5059' }}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z"
              ></path>
            </svg>
          </div>
        )}

        {status === 'reset' && (
          <div>
            <h2 
              className="text-2xl font-bold mb-4"
              style={{ color: '#2D2D2D' }}
            >
              Reset Your Password
            </h2>
            <form onSubmit={handlePasswordReset}>
              <div className="mb-4">
                <input
                  type="password"
                  value={newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                  className="w-full p-3 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2"
                  style={{ 
                    borderColor: validationErrors.password ? '#E5484D' : '#EAECF0',
                    backgroundColor: '#FCFCFD',
                    color: '#2D2D2D'
                  }}
                  onFocus={(e) => e.target.style.borderColor = validationErrors.password ? '#E5484D' : '#FC5059'}
                  onBlur={(e) => e.target.style.borderColor = validationErrors.password ? '#E5484D' : '#EAECF0'}
                />
                {validationErrors.password && (
                  <div className="mt-2 text-sm" style={{ color: '#E5484D' }}>
                    {Object.values(validationErrors.password).map((error, index) => (
                      <div key={index}>• {error}</div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="mb-4">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="Confirm new password"
                  required
                  className="w-full p-3 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2"
                  style={{ 
                    borderColor: validationErrors.confirm ? '#E5484D' : '#EAECF0',
                    backgroundColor: '#FCFCFD',
                    color: '#2D2D2D'
                  }}
                  onFocus={(e) => e.target.style.borderColor = validationErrors.confirm ? '#E5484D' : '#FC5059'}
                  onBlur={(e) => e.target.style.borderColor = validationErrors.confirm ? '#E5484D' : '#EAECF0'}
                />
                {validationErrors.confirm && (
                  <div className="mt-2 text-sm" style={{ color: '#E5484D' }}>
                    • {validationErrors.confirm}
                  </div>
                )}
              </div>
              
              <div className="mb-6">
                <div className="text-sm" style={{ color: '#425466' }}>
                  <div className="mb-2 font-medium">Password Requirements:</div>
                  <div className="space-y-1">
                    <div className={`flex items-center ${newPassword.length >= 8 ? 'text-green-600' : ''}`}>
                      <span className="mr-2">{newPassword.length >= 8 ? '✓' : '•'}</span>
                      At least 8 characters
                    </div>
                    <div className={`flex items-center ${/(?=.*[a-z])/.test(newPassword) ? 'text-green-600' : ''}`}>
                      <span className="mr-2">{/(?=.*[a-z])/.test(newPassword) ? '✓' : '•'}</span>
                      One lowercase letter
                    </div>
                    <div className={`flex items-center ${/(?=.*[A-Z])/.test(newPassword) ? 'text-green-600' : ''}`}>
                      <span className="mr-2">{/(?=.*[A-Z])/.test(newPassword) ? '✓' : '•'}</span>
                      One uppercase letter
                    </div>
                    <div className={`flex items-center ${/(?=.*\d)/.test(newPassword) ? 'text-green-600' : ''}`}>
                      <span className="mr-2">{/(?=.*\d)/.test(newPassword) ? '✓' : '•'}</span>
                      One number
                    </div>
                    <div className={`flex items-center ${/(?=.*[@$!%*?&])/.test(newPassword) ? 'text-green-600' : ''}`}>
                      <span className="mr-2">{/(?=.*[@$!%*?&])/.test(newPassword) ? '✓' : '•'}</span>
                      One special character (@$!%*?&)
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={Object.keys(validationErrors).some(key => validationErrors[key]) || !newPassword || !confirmPassword}
                className="w-full px-4 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                style={{ 
                  backgroundColor: '#FC5059', 
                  color: '#FCFCFD' 
                }}
              >
                Reset Password
              </button>
            </form>
          </div>
        )}

        {status === 'success' && (
          <div>
            <h2 
              className="text-2xl font-bold mb-4"
              style={{ color: '#2D2D2D' }}
            >
              Password Reset Successfully!
            </h2>
            <p 
              className="mb-4"
              style={{ color: '#425466' }}
            >
              Your password has been updated. Please click the link below to go to the login page.
            </p>
            <button
              onClick={handleLoginRedirect}
              className="w-full px-4 py-3 rounded-lg font-medium transition-colors duration-200 hover:opacity-90"
              style={{ 
                backgroundColor: '#FC5059', 
                color: '#FCFCFD' 
              }}
            >
              Go to Login
            </button>
          </div>
        )}

        {status === 'error' && (
          <div>
            <h2 
              className="text-2xl font-bold mb-4"
              style={{ color: '#E5484D' }}
            >
              Oops, something went wrong!
            </h2>
            <p 
              className="mb-4"
              style={{ color: '#425466' }}
            >
              {errorMessage}
            </p>
            <button
              onClick={handleLoginRedirect}
              className="w-full px-4 py-3 rounded-lg font-medium transition-colors duration-200 hover:opacity-90"
              style={{ 
                backgroundColor: '#FC5059', 
                color: '#FCFCFD' 
              }}
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reset;